import { useState } from "react";
import { AlertCircle, Globe, Loader2, Phone, PhoneCall, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { Assistant, AssistantCall } from "../api";
import { useAssistantCalls } from "../hooks";
import CallDetailSheet from "./CallDetailSheet";

const STATUS_STYLES: Record<string, { label: string; cls: string }> = {
  completed: {
    label: "Completed",
    cls: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400",
  },
  failed: {
    label: "Failed",
    cls: "text-rose-600 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-400",
  },
  in_progress: {
    label: "In progress",
    cls: "text-blue-600 bg-blue-50 dark:bg-blue-950/40 dark:text-blue-400",
  },
  pending: {
    label: "Pending",
    cls: "text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300",
  },
};

function fmtDuration(seconds: number | null | undefined) {
  const val = seconds ?? 0;
  const m = Math.floor(val / 60);
  const s = val % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function fmtDate(call: AssistantCall) {
  const iso = call.started_at || call.created_at;
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) && t !== 0 ? new Date(t).toLocaleString() : "—";
}

/** Calls made to this assistant (today: browser Talk sessions). Rows open the
 * full call detail in a slide-over so the Logs tab stays put. */
export default function AssistantCallLogs({ assistant }: { assistant: Assistant }) {
  const [openCallId, setOpenCallId] = useState<string | null>(null);
  const q = useAssistantCalls(assistant.id);
  const calls = q.data ?? [];

  return (
    <div className="rounded-xl border bg-card">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <div className="text-sm font-semibold">Calls</div>
          <p className="text-xs text-muted-foreground">
            Calls handled by this assistant — Talk sessions for now, phone calls
            once numbers are attached.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => q.refetch()}
          disabled={q.isFetching}
        >
          {q.isFetching ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <RefreshCw className="size-3.5" />
          )}
          Refresh
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Started</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Direction</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Duration</th>
              <th className="px-4 py-3 font-medium">Sentiment</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {q.isLoading &&
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={6} className="px-4 py-2">
                    <Skeleton className="h-6 w-full" />
                  </td>
                </tr>
              ))}

            {!q.isLoading && q.isError && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm">
                  <AlertCircle className="mx-auto mb-2 size-6 text-rose-500" />
                  <p className="text-muted-foreground">Couldn't load the call log.</p>
                </td>
              </tr>
            )}

            {!q.isLoading && !q.isError && calls.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-10 text-center text-sm text-muted-foreground"
                >
                  <PhoneCall className="mx-auto mb-1 size-6 opacity-50" />
                  No calls yet — use Talk to have the first conversation.
                </td>
              </tr>
            )}

            {!q.isLoading && !q.isError &&
              calls.map((c) => {
                const status = STATUS_STYLES[c.status] ?? {
                  label: c.status,
                  cls: "text-muted-foreground bg-muted",
                };
                return (
                  <tr
                    key={c.id}
                    className="cursor-pointer hover:bg-accent/40"
                    onClick={() => setOpenCallId(c.id)}
                  >
                    <td className="px-4 py-2.5 text-muted-foreground">{fmtDate(c)}</td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        {c.type === "web" ? (
                          <>
                            <Globe className="size-3.5" />
                            Web
                          </>
                        ) : (
                          <>
                            <Phone className="size-3.5" />
                            Phone
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 capitalize text-muted-foreground">
                      {c.type === "web" ? "—" : c.direction || "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                          status.cls,
                        )}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-muted-foreground">
                      {fmtDuration(c.duration_seconds)}
                    </td>
                    <td className="px-4 py-2.5 capitalize text-muted-foreground">
                      {c.sentiment || "—"}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <CallDetailSheet callId={openCallId} onClose={() => setOpenCallId(null)} />
    </div>
  );
}
